/**
 * DynamicWizard Component
 * 
 * Universal multi-page wizard renderer
 * - Reads wizard template from Question Bank
 * - Renders questions per page
 * - Handles conditional logic
 * - Auto-saves progress
 * - Tracks completion status
 * - Supports "Ready for Build" change warnings
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { ChevronLeft, ChevronRight, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { QuestionRenderer } from './QuestionRenderer';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import type { Question } from '../supabase/functions/server/questionBankEnhanced';
import { formatAuthorityName } from '../supabase/functions/server/authorityLabeling';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface WizardPage {
  id: string;
  title: string;
  description?: string;
  questionIds: string[];
  questions?: Question[];
}

interface WizardTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  pages: WizardPage[];
  allowSkip: boolean;
  showProgress: boolean;
  autoSave: boolean;
  published: boolean;
}

interface DynamicWizardProps {
  open: boolean;
  onClose: () => void;
  wizardId: string;
  configItemId?: string;
  companyId: string;
  sowId?: string;
  consultingFirmId: string;
  onComplete?: () => void;
  initialAnswers?: Record<string, any>;
  returnTo?: 'grid' | 'category' | 'dashboard';
  userRole: 'client' | 'consultant';
  readyForBuildWarning?: boolean; // Show warning if CI is in ready_for_build status
}

export function DynamicWizard({
  open,
  onClose,
  wizardId,
  configItemId,
  companyId,
  sowId,
  consultingFirmId,
  onComplete,
  initialAnswers = {},
  returnTo = 'grid',
  userRole,
  readyForBuildWarning = false
}: DynamicWizardProps) {
  const [wizard, setWizard] = useState<WizardTemplate | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>(initialAnswers);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  // Load wizard template
  useEffect(() => {
    if (open && wizardId) {
      loadWizard();
    }
  }, [open, wizardId]);

  // Load existing session if resuming
  useEffect(() => {
    if (open && configItemId && wizardId) {
      loadWizardSession();
    }
  }, [open, configItemId, wizardId]);

  const loadWizard = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/${wizardId}`,
        {
          headers: {
            ...bearerAuthHeader(),
            'x-consulting-firm-id': consultingFirmId
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWizard(data.wizard);
      } else if (response.status === 400 || response.status === 404) {
        // Wizard template doesn't exist yet - show helpful error
        console.log('[DynamicWizard] Wizard template not found, API not implemented yet');
        toast.error('Discovery wizard not yet configured for this category');
        onClose();
      } else {
        toast.error('Failed to load wizard template');
        onClose();
      }
    } catch (error) {
      console.error('Error loading wizard:', error);
      toast.error('Error loading wizard');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const loadWizardSession = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${configItemId}/wizard-session/${wizardId}`,
        {
          headers: {
            ...bearerAuthHeader()
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.session) {
          setCurrentPage(data.session.currentPage || 0);
          // Load answers from config item
          loadConfigItemAnswers();
        }
      }
    } catch (error) {
      console.error('Error loading wizard session:', error);
    }
  };

  const loadConfigItemAnswers = async () => {
    if (!configItemId) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${configItemId}`,
        {
          headers: {
            ...bearerAuthHeader()
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.configItem?.answers) {
          const answerMap: Record<string, any> = {};
          data.configItem.answers.forEach((a: any) => {
            answerMap[a.questionId] = a.answer;
          });
          setAnswers(answerMap);
        }
      }
    } catch (error) {
      console.error('Error loading config item answers:', error);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Clear error for this question
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });

    // Auto-save if enabled
    if (wizard?.autoSave && configItemId) {
      debouncedAutoSave();
    }
  };

  let autoSaveTimeout: NodeJS.Timeout;
  const debouncedAutoSave = () => {
    clearTimeout(autoSaveTimeout);
    autoSaveTimeout = setTimeout(() => {
      saveProgress();
    }, 2000);
  };

  const saveProgress = async () => {
    if (!configItemId || !wizard) return;

    try {
      setSaving(true);
      
      // Update wizard session
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${configItemId}/wizard-session`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
            'x-consulting-firm-id': consultingFirmId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            wizardId,
            wizardName: (wizard as any)?.name,
            companyId,
            currentPage,
            totalPages: wizard.pages.length,
            status: 'in_progress'
          })
        }
      );

      // Save answers to config item
      await saveAnswersToConfigItem();

      toast.success('Progress saved', { duration: 1500 });
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveAnswersToConfigItem = async () => {
    if (!configItemId) return;

    const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
      questionId,
      answer,
      answeredBy: userRole === 'client' ? companyId : consultingFirmId,
      answeredAt: new Date().toISOString(),
      source: 'wizard'
    }));

    await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${configItemId}/answers`,
      {
        method: 'PUT',
        headers: {
          ...bearerAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: answersArray })
      }
    );
  };

  const validatePage = (page: WizardPage): boolean => {
    if (!page.questions) return true;

    const newErrors: Record<string, string> = {};
    let isValid = true;

    for (const question of page.questions) {
      // Check conditional visibility
      if (question.conditionalOn) {
        const conditionMet = checkCondition(question.conditionalOn);
        if (!conditionMet) continue; // Skip validation if not visible
      }

      // Check required fields
      if (question.required) {
        const answer = answers[question.id];
        if (answer === undefined || answer === null || answer === '') {
          newErrors[question.id] = 'This field is required';
          isValid = false;
        }
      }

      // Validate number ranges
      if (question.type === 'number' && question.validation) {
        const answer = answers[question.id];
        if (answer !== undefined && answer !== null && answer !== '') {
          const num = Number(answer);
          if (question.validation.min !== undefined && num < question.validation.min) {
            newErrors[question.id] = `Must be at least ${question.validation.min}`;
            isValid = false;
          }
          if (question.validation.max !== undefined && num > question.validation.max) {
            newErrors[question.id] = `Must be at most ${question.validation.max}`;
            isValid = false;
          }
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const checkCondition = (condition: any): boolean => {
    if (!condition) return true;

    const parentAnswer = answers[condition.questionId];
    const expectedValue = condition.value ?? condition.expectedValue;

    if (condition.operator === 'equals' || condition.operator === 'eq' || !condition.operator) {
      return parentAnswer === expectedValue;
    }
    
    if (condition.operator === 'contains') {
      return String(parentAnswer).includes(String(expectedValue));
    }
    
    if (condition.operator === 'includes') {
      if (Array.isArray(parentAnswer)) {
        return parentAnswer.includes(expectedValue);
      }
      return false;
    }

    if (condition.operator === 'greaterThan') {
      return Number(parentAnswer) > Number(expectedValue);
    }

    return false;
  };

  const handleNext = () => {
    if (!wizard) return;

    const currentPageData = wizard.pages[currentPage];
    if (!validatePage(currentPageData)) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (currentPage < wizard.pages.length - 1) {
      setCurrentPage(currentPage + 1);
      saveProgress();
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      saveProgress();
    }
  };

  const handleSaveAndExit = async () => {
    await saveProgress();
    onClose();
  };

  const handleComplete = async () => {
    if (!wizard) return;

    const lastPage = wizard.pages[wizard.pages.length - 1];
    if (!validatePage(lastPage)) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if editing ready-for-build item
    if (readyForBuildWarning && !warningAcknowledged) {
      setShowWarning(true);
      return;
    }

    try {
      setSaving(true);

      // Mark wizard as completed
      if (configItemId) {
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${configItemId}/wizard-session`,
          {
            method: 'POST',
            headers: {
              ...bearerAuthHeader(),
              'x-consulting-firm-id': consultingFirmId,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              wizardId,
              wizardName: (wizard as any)?.name,
              companyId,
              currentPage,
              totalPages: wizard.pages.length,
              status: 'completed'
            })
          }
        );
      }

      // Save final answers
      await saveAnswersToConfigItem();

      toast.success('Wizard completed');
      
      if (onComplete) {
        onComplete();
      }
      
      onClose();
    } catch (error) {
      console.error('Error completing wizard:', error);
      toast.error('Failed to complete wizard');
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = (): number => {
    if (!wizard) return 0;
    
    const totalPages = wizard.pages.length;
    const completedPages = currentPage;
    
    // Get all required questions up to current page
    const questionsUpToCurrent = wizard.pages
      .slice(0, currentPage + 1)
      .flatMap(p => p.questions || []);
    
    const requiredQuestions = questionsUpToCurrent.filter(q => q.required);
    const answeredRequired = requiredQuestions.filter(q => {
      const answer = answers[q.id];
      return answer !== undefined && answer !== null && answer !== '';
    });

    const pageProgress = ((completedPages + 1) / totalPages) * 50;
    const answerProgress = requiredQuestions.length > 0
      ? (answeredRequired.length / requiredQuestions.length) * 50
      : 50;

    return Math.round(pageProgress + answerProgress);
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Loading Wizard</DialogTitle>
            <DialogDescription>Please wait while we load the discovery questions...</DialogDescription>
          </DialogHeader>
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading wizard...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!wizard) return null;

  const currentPageData = wizard.pages[currentPage];
  const visibleQuestions = currentPageData.questions?.filter(q => {
    // 1. Legacy check
    if (q.conditionalOn) {
      if (!checkCondition(q.conditionalOn)) return false;
    }

    // 2. conditionalOnAll (AND)
    if (q.conditionalOnAll && q.conditionalOnAll.length > 0) {
      const allPass = q.conditionalOnAll.every((cond: any) => checkCondition(cond));
      if (!allPass) return false;
    }

    // 3. conditionalOnAny (OR)
    if (q.conditionalOnAny && q.conditionalOnAny.length > 0) {
      const anyPass = q.conditionalOnAny.some((cond: any) => checkCondition(cond));
      if (!anyPass) return false;
    }

    return true;
  }) || [];

  const progress = calculateProgress();
  const answeredCount = visibleQuestions.filter(q => {
    const answer = answers[q.id];
    return answer !== undefined && answer !== null && answer !== '';
  }).length;

  const processQuestion = (q: Question): Question => {
    if (q.optionsFromAnswer) {
      const sourceQId = q.optionsFromAnswer.questionId;
      const sourceValue = answers[sourceQId];
      
      const dynamicOptions: Array<{ value: string; label: string; description?: string }> = [];

      if (Array.isArray(sourceValue)) {
        // 1. Toolset-derived tool options
        // Filter out 'not_sure' and 'other' from source for main list
        // Filter out duplicates
        const uniqueTools = Array.from(new Set(sourceValue));
        const validTools = uniqueTools.filter((v: string) => v !== 'not_sure' && v !== 'other');
        
        validTools.forEach((v: string) => {
          dynamicOptions.push({
            value: v,
            label: formatAuthorityName(v)
          });
        });
        
        // 2. Optional "Other (from your list)"
        if (sourceValue.includes('other')) {
          const otherText = answers[`${sourceQId}_other`];
          dynamicOptions.push({
            value: 'other_toolset',
            label: 'Other (from your list)',
            description: otherText || '(No description provided)'
          });
        }
      }

      // 3. 'other' = "Other"
      dynamicOptions.push({ value: 'other', label: 'Other' });

      // 4. 'not_sure' = "Not sure yet"
      dynamicOptions.push({ value: 'not_sure', label: 'Not sure yet' });

      // B. Replace options entirely
      return {
        ...q,
        options: dynamicOptions
      };
    }
    return q;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{wizard.name}</DialogTitle>
            <DialogDescription>{wizard.description}</DialogDescription>
          </DialogHeader>

          {/* Progress Bar */}
          {wizard.showProgress && (
            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Step {currentPage + 1} of {wizard.pages.length}
                </span>
                <span className="font-medium">{progress}% complete</span>
              </div>
              <Progress value={progress} className="h-2" />
              <div className="text-xs text-muted-foreground text-right">
                {answeredCount} of {visibleQuestions.length} questions answered
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{currentPageData.title}</Badge>
              {saving && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>
            {currentPageData.description && (
              <p className="text-sm text-muted-foreground">{currentPageData.description}</p>
            )}
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {visibleQuestions.map((question) => (
              <QuestionRenderer
                key={question.id}
                question={processQuestion(question)}
                value={answers[question.id]}
                onChange={(value) => handleAnswerChange(question.id, value)}
                error={errors[question.id]}
                disabled={saving}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <div className="flex gap-2">
              {currentPage > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={saving}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={handleSaveAndExit}
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                Save & Exit
              </Button>

              {currentPage < wizard.pages.length - 1 ? (
                <Button onClick={handleNext} disabled={saving}>
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleComplete} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    'Complete Wizard'
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ready for Build Warning Dialog */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Warning: Implementation Has Begun
            </DialogTitle>
          </DialogHeader>

          <Alert className="border-orange-500 bg-orange-50">
            <AlertDescription className="text-sm">
              Changing this configuration item after implementation has begun will cause the need 
              for re-work and may contribute to the need for a change order to handle the additional scope.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowWarning(false);
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setWarningAcknowledged(true);
                setShowWarning(false);
                handleComplete();
              }}
            >
              I Understand, Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}